import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/** The workspace with the copy of a version that a save returned in place of the one it held. */
export const withSavedArtifact = <W extends { artifacts: Artifact[] }>(
  workspace: W,
  saved: Artifact
): W => ({
  ...workspace,
  artifacts: workspace.artifacts.map((each) =>
    each.id === saved.id && each.version === saved.version ? saved : each
  )
});

/**
 * Whether a version differs from the one before it only in its design, as one a design switch
 * made does. The switch stamps editedAt the way an edit does, though no words changed.
 */
export const changedOnlyDesign = (artifact: Artifact, versions: readonly Artifact[]): boolean => {
  const previous = versions.find(
    (each) => each.id === artifact.id && each.version === artifact.version - 1
  );
  return (
    !!previous &&
    (previous.document?.designId ?? null) !== (artifact.document?.designId ?? null) &&
    withoutHexFallbacks(previous.document?.content) ===
      withoutHexFallbacks(artifact.document?.content)
  );
};

/**
 * The switch moves each brand fallback to the new palette (brand.RebrandFallbacks on the
 * server), so those hexes are not an edit. pkg/service/api/brand/testdata/rebrand_fallbacks.json
 * holds this and the server's rewrite to the same cases.
 */
const withoutHexFallbacks = (content: string | undefined) =>
  content?.replace(/var\(\s*(--[\w-]+)\s*,\s*#[0-9a-f]{3}(?:[0-9a-f]{3})?\s*\)/gi, 'var($1)');
